// DebugSlice.ts
import { IMcBimItem } from "@backend/src/schemas";
import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { convertMcBimItemToPixPolygons, convertMcBimSpaceToPixPolygon } from "../utils";
import { IPixPolygon } from "../components/PixBimViewer/types";

// --- Types ---
export type ItemsToShow = "all" | "spaces" | "non-spaces";

export interface IDebugGeometry {
  expressId: number;
  globalId: string;
  instanceClass: string;
  geometries: Array<{
    geometryExpressID: number;
    transformationMatrix: number[];
    vertexData: number[];
    indexData: number[];
  }>;
  freeTextSearch: string;
}

export type DebugState = {
  items: IMcBimItem[];
  activeBuildingStoryName: string | null; // null = none selected
  itemsToShow: ItemsToShow;

  /** Lista av IFC-typklasser som ska döljas (tom lista = visa alla). */
  hiddenTypeClasses: string[];

  // 🆕 3D-data från debug JSON-filer
  threeDItems: IDebugGeometry[];
  lastDroppedFileName: string | null; // ← NYTT
  freeTextSearch: string;
};

// --- LocalStorage keys ---
const LOCAL_STORAGE_KEY = "debugState";

// --- Helper to save to localStorage ---
const saveToLocalStorage = (state: DebugState) => {
  try {
    const dataToSave = {
      items: state.items,
      threeDItems: state.threeDItems,
    };
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(dataToSave));
  } catch (error) {
    console.error("Error saving to localStorage:", error);
  }
};

// --- Load from localStorage for initialState ---
let loadedInitialState: Partial<DebugState> = {};
try {
  const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (stored) {
    loadedInitialState = JSON.parse(stored);
  }
} catch (error) {
  console.error("Error loading from localStorage:", error);
}

// --- Initial state ---
const initialState: DebugState = {
  items: loadedInitialState.items ?? [],
  activeBuildingStoryName: null,
  itemsToShow: "all",
  hiddenTypeClasses: [], // tom = ingen döljs
  threeDItems: loadedInitialState.threeDItems ?? [], // 🆕 tom lista för 3D-data
  lastDroppedFileName: null,
  freeTextSearch: "",
};

// --- Slice ---
export const debugSlice = createSlice({
  name: "debug",
  initialState,
  reducers: {
    // Replace all (most common after JSON drop)
    setDebugMcBimItemsInRedux(state, action: PayloadAction<IMcBimItem[]>) {
      state.items = action.payload;
      //saveToLocalStorage(state);
    },
    // Append many
    appendDebugMcBimItemsInRedux(state, action: PayloadAction<IMcBimItem[]>) {
      state.items.push(...action.payload);
      saveToLocalStorage(state);
    },
    // Clear
    clearDebugMcBimItemsInRedux(state) {
      state.items = [];
      saveToLocalStorage(state);
    },

    // 🆕 3D-reducers (liknande 2D)
    setThreeDItemsInRedux(state, action: PayloadAction<IDebugGeometry[]>) {
      state.threeDItems = action.payload;
      saveToLocalStorage(state);
    },
    appendThreeDItemsInRedux(state, action: PayloadAction<IDebugGeometry[]>) {
      state.threeDItems.push(...action.payload);
      saveToLocalStorage(state);
    },
    clearThreeDItemsInRedux(state) {
      state.threeDItems = [];
      saveToLocalStorage(state);
    },

    // --- Active building storey ---
    setActiveBuildingStoryNameInRedux(state, action: PayloadAction<string | null>) {
      state.activeBuildingStoryName = action.payload; // set null to unselect
    },
    clearActiveBuildingStoryNameInRedux(state) {
      state.activeBuildingStoryName = null;
    },
    setDebugFreeTextSearchInRedux(state, action: PayloadAction<string>) {
      state.freeTextSearch = (action.payload ?? "").trim();
    },
    // --- Items to show filter ---
    setItemsToShowInRedux(state, action: PayloadAction<ItemsToShow>) {
      state.itemsToShow = action.payload;
    },

    // --- IFC typeClass HIDE-filter ---
    /** Sätter exakt de typklasser som ska döljas (tom = dölj inga). */
    setHiddenTypeClassesInRedux(state, action: PayloadAction<string[]>) {
      const set = new Set(
        (action.payload ?? [])
          .map((s) => (typeof s === "string" ? s.trim() : ""))
          .filter((s) => s.length > 0)
      );
      state.hiddenTypeClasses = Array.from(set);
    },
    /** Töm filtret (dölj inga). */
    clearHiddenTypeClassesInRedux(state) {
      state.hiddenTypeClasses = [];
    },
    /** Toggle för en enskild typeClass. */
    toggleHiddenTypeClassInRedux(state, action: PayloadAction<string>) {
      const key = (action.payload ?? "").trim();
      if (!key) return;
      const idx = state.hiddenTypeClasses.indexOf(key);
      if (idx >= 0) {
        state.hiddenTypeClasses.splice(idx, 1);
      } else {
        state.hiddenTypeClasses.push(key);
      }
    },
    // ← NY reducer för att spara filnamnet
    setLastDroppedFileName(state, action: PayloadAction<string>) {
      state.lastDroppedFileName = action.payload;
    },
  },
});

export const {
  setDebugMcBimItemsInRedux,
  appendDebugMcBimItemsInRedux,
  clearDebugMcBimItemsInRedux,
  setThreeDItemsInRedux,
  appendThreeDItemsInRedux,
  clearThreeDItemsInRedux,
  setActiveBuildingStoryNameInRedux,
  clearActiveBuildingStoryNameInRedux,
  setItemsToShowInRedux,
  setHiddenTypeClassesInRedux,
  clearHiddenTypeClassesInRedux,
  toggleHiddenTypeClassInRedux,
  setLastDroppedFileName,
  setDebugFreeTextSearchInRedux,
} = debugSlice.actions;

export default debugSlice.reducer;

// --- Selectors ---
export const selectDebugState = (s: { debug: DebugState }) => s.debug;
export const selectDebugMcbimItems = (s: { debug: DebugState }) => s.debug.items;
export const selectDebugActiveBuildingStoryName = (s: { debug: DebugState }) =>
  s.debug.activeBuildingStoryName;
export const selectDebugItemsToShow = (s: { debug: DebugState }) => s.debug.itemsToShow;
export const selectDebugHiddenTypeClasses = (s: { debug: DebugState }) => s.debug.hiddenTypeClasses;

// 🆕 Selector för 3D-data
export const selectDebugThreeDItems = (s: { debug: DebugState }) => s.debug.threeDItems;
